import { getArchLayout } from '../constants/odontology';
import type { PeriodontalChart, PeriodontalMeasurements } from '../types/odontology';

function formatDateBr(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR');
}

const EXAM_TYPE_LABELS: Record<string, string> = {
  initial: 'Exame inicial',
  reevaluation: 'Reavaliação',
  maintenance: 'Manutenção',
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function toothRow(
  teeth: string[],
  measurements: PeriodontalMeasurements,
  label: string,
): string {
  const cells = teeth.map((tooth) => {
    const data = measurements[tooth];
    if (!data || data.absent) {
      return `<td colspan="3" style="text-align:center;color:#94A3B8;">—</td>`;
    }
    const buccal = data.buccal || {};
    const sites = ['mesial', 'central', 'distal'] as const;
    return sites.map((site) => {
      const m = buccal[site];
      const pd = m?.pd ?? '—';
      const bop = m?.bop ? '●' : '';
      const color = typeof pd === 'number' && pd >= 6 ? '#DC2626' : typeof pd === 'number' && pd >= 4 ? '#D97706' : '#0F172A';
      return `<td style="text-align:center;font-weight:600;color:${color};">${pd}${bop ? `<span style="color:#DC2626;font-size:9px;">${bop}</span>` : ''}</td>`;
    }).join('');
  }).join('');

  return `
    <tr>
      <td style="font-weight:600;background:#F8FAFC;">${escapeHtml(label)}</td>
      ${cells}
    </tr>
  `;
}

function archTable(title: string, teeth: string[], measurements: PeriodontalMeasurements): string {
  const header = teeth.map((t) => `<th colspan="3" style="text-align:center;padding:4px;border:1px solid #E2E8F0;">${t}</th>`).join('');
  const sub = teeth.map(() => '<th style="font-size:9px;border:1px solid #F1F5F9;">M</th><th style="font-size:9px;border:1px solid #F1F5F9;">C</th><th style="font-size:9px;border:1px solid #F1F5F9;">D</th>').join('');

  return `
    <h3 style="margin:16px 0 8px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:#64748B;">${escapeHtml(title)}</h3>
    <table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:12px;">
      <thead>
        <tr><th rowspan="2" style="text-align:left;padding:4px;border:1px solid #E2E8F0;background:#F8FAFC;">PS vestibular (mm)</th>${header}</tr>
        <tr>${sub}</tr>
      </thead>
      <tbody>
        ${toothRow(teeth, measurements, 'Vestibular')}
      </tbody>
    </table>
  `;
}

export function buildPeriodontalPrintHtml(payload: {
  chart: PeriodontalChart;
  clinic?: { clinic_name?: string; logo_url?: string };
}): string {
  const { chart, clinic } = payload;
  const arch = getArchLayout('permanent');
  const upperTeeth = [...arch.upper.right, ...arch.upper.left];
  const lowerTeeth = [...arch.lower.right, ...arch.lower.left];
  const measurements = chart.measurements || {};
  const summary = chart.summary || {};

  const clinicName = clinic?.clinic_name || 'Clínica';
  const patientName = chart.patient_name || 'Paciente';
  const examLabel = EXAM_TYPE_LABELS[chart.exam_type] || chart.exam_type;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>Periodontograma — ${escapeHtml(patientName)}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; color: #0F172A; font-size: 11px; line-height: 1.45; }
    h1 { font-size: 18px; margin: 0 0 4px; letter-spacing: -0.02em; }
    .meta { color: #64748B; margin-bottom: 16px; }
    .kpis { display: flex; flex-wrap: wrap; gap: 12px; margin: 16px 0; }
    .kpi { border: 1px solid #E2E8F0; border-radius: 8px; padding: 8px 12px; min-width: 100px; }
    .kpi label { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #94A3B8; }
    .kpi strong { font-size: 14px; }
    .notes { margin-top: 16px; padding: 12px; background: #F8FAFC; border-radius: 8px; border: 1px solid #E2E8F0; }
    .footer { margin-top: 24px; font-size: 9px; color: #94A3B8; text-align: center; }
  </style>
</head>
<body>
  <h1>Periodontograma</h1>
  <div class="meta">
    <strong>${escapeHtml(clinicName)}</strong><br />
    Paciente: ${escapeHtml(patientName)}<br />
    ${escapeHtml(examLabel)} · ${escapeHtml(formatDateBr(chart.exam_date))}
    ${chart.professional_name ? ` · ${escapeHtml(chart.professional_name)}` : ''}
  </div>

  <div class="kpis">
    <div class="kpi"><label>Índice de placa</label><strong>${summary.plaque_index ?? 0}%</strong></div>
    <div class="kpi"><label>Sangramento</label><strong>${summary.bleeding_index ?? 0}%</strong></div>
    <div class="kpi"><label>PS média</label><strong>${summary.mean_probing_depth ?? 0} mm</strong></div>
    <div class="kpi"><label>NIC médio</label><strong>${summary.mean_attachment_loss ?? 0} mm</strong></div>
    <div class="kpi"><label>Sítios 4–5 mm</label><strong>${summary.sites_pd_4_5 ?? 0}</strong></div>
    <div class="kpi"><label>Sítios ≥ 6 mm</label><strong style="color:#DC2626;">${summary.sites_pd_6_plus ?? 0}</strong></div>
    <div class="kpi"><label>Dentes examinados</label><strong>${summary.teeth_examined ?? 0}</strong></div>
  </div>

  ${archTable('Arcada superior — sondagem vestibular', upperTeeth, measurements)}
  ${archTable('Arcada inferior — sondagem vestibular', lowerTeeth, measurements)}

  <p style="font-size:9px;color:#94A3B8;">● = sangramento à sondagem · M/C/D = mesial, central, distal</p>

  ${chart.diagnosis ? `<div class="notes"><strong>Diagnóstico</strong><br />${escapeHtml(chart.diagnosis)}</div>` : ''}
  ${chart.notes ? `<div class="notes" style="margin-top:8px;"><strong>Observações</strong><br />${escapeHtml(chart.notes)}</div>` : ''}

  <div class="footer">Documento gerado pelo Klinvia · ${new Date().toLocaleString('pt-BR')}</div>
</body>
</html>`;
}
